/**
 * Meta (Facebook / Instagram) in-app browser: IP geolocation is often wrong (e.g. NZ → UK/GBP).
 * 1) Device language with region (en-NZ, fr-CA, etc.)
 * 2) Device timezone when English is generic (en, en-US, en-GB) — catches CA, AU, NZ, ZA
 * Only runs in Meta in-app browsers; once per session.
 */
(function () {
  'use strict';

  var SESSION_KEY = 'joga_meta_inapp_loc_v2';
  var config = window.JOGA_INAPP_LOC;

  if (!config || !config.availableCountries || !config.availableCountries.length) {
    return;
  }

  function isMetaInAppBrowser() {
    var ua = navigator.userAgent || '';
    return (
      /FBAN|FBAV|Instagram|FB_IAB|FB4A|FBIOS|Messenger/i.test(ua) ||
      (/\bFB\b/i.test(ua) && /Mobile/i.test(ua))
    );
  }

  if (!isMetaInAppBrowser()) {
    return;
  }

  if (sessionStorage.getItem(SESSION_KEY)) {
    return;
  }

  if (new URLSearchParams(window.location.search).has('skip_inapp_loc')) {
    return;
  }

  var available = Object.create(null);
  config.availableCountries.forEach(function (code) {
    available[code] = true;
  });

  /** Do not infer GB/US from generic English locale tags (common mis-set phones). */
  var EN_AMBIGUOUS_REGIONS = { GB: true, US: true };

  var LANG_TO_COUNTRY = {
    fr: 'FR',
    de: 'DE',
    es: 'ES',
    it: 'IT',
    nl: 'NL',
    ja: 'JP',
    ko: 'KR',
    zh: 'CN',
    ar: 'AE',
    pl: 'PL',
    sv: 'SE',
    da: 'DK',
    fi: 'FI',
    nb: 'NO',
    no: 'NO',
    pt: 'PT',
    ru: 'RU',
    tr: 'TR',
    hi: 'IN',
    th: 'TH',
    vi: 'VN',
    id: 'ID',
    ms: 'MY',
    cs: 'CZ',
    hu: 'HU',
    ro: 'RO',
    el: 'GR',
    he: 'IL',
    uk: 'UA',
    am: 'ET',
    sr: 'RS',
    tg: 'TJ',
    ur: 'PK',
    zu: 'ZA',
  };

  /** IANA timezone → country for generic-English fallback (paid social traffic). */
  var TZ_PREFIX_TO_COUNTRY = [
    { prefix: 'Australia/', country: 'AU' },
    { prefix: 'Pacific/Auckland', country: 'NZ' },
    { prefix: 'Pacific/Chatham', country: 'NZ' },
    { prefix: 'Africa/Johannesburg', country: 'ZA' },
    { prefix: 'America/Toronto', country: 'CA' },
    { prefix: 'America/Vancouver', country: 'CA' },
    { prefix: 'America/Winnipeg', country: 'CA' },
    { prefix: 'America/Edmonton', country: 'CA' },
    { prefix: 'America/Halifax', country: 'CA' },
    { prefix: 'America/St_Johns', country: 'CA' },
    { prefix: 'America/Regina', country: 'CA' },
    { prefix: 'America/Whitehorse', country: 'CA' },
    { prefix: 'America/Yellowknife', country: 'CA' },
    { prefix: 'America/Iqaluit', country: 'CA' },
    { prefix: 'America/Moncton', country: 'CA' },
    { prefix: 'America/Rankin_Inlet', country: 'CA' },
    { prefix: 'America/Resolute', country: 'CA' },
    { prefix: 'America/Cambridge_Bay', country: 'CA' },
    { prefix: 'America/Inuvik', country: 'CA' },
    { prefix: 'America/Dawson', country: 'CA' },
    { prefix: 'America/Dawson_Creek', country: 'CA' },
    { prefix: 'America/Fort_Nelson', country: 'CA' },
    { prefix: 'America/Creston', country: 'CA' },
    { prefix: 'America/Atikokan', country: 'CA' },
    { prefix: 'America/Blanc-Sablon', country: 'CA' },
  ];

  function normalizeTag(tag) {
    return (tag || '').trim().replace(/_/g, '-');
  }

  function countryFromTag(tag) {
    var normalized = normalizeTag(tag);
    if (!normalized) {
      return null;
    }

    var parts = normalized.split('-');
    var lang = parts[0].toLowerCase();

    if (lang === 'en' && parts.length < 2) {
      return null;
    }

    if (parts.length >= 2) {
      var sub = parts[1].toLowerCase();

      if (lang === 'pt' && sub === 'br' && available.BR) {
        return 'BR';
      }

      if (lang === 'zh') {
        if ((sub === 'tw' || sub === 'hant') && available.TW) {
          return 'TW';
        }
        if (sub === 'hk' && available.HK) {
          return 'HK';
        }
        if (sub === 'cn' && available.CN) {
          return 'CN';
        }
      }

      var region = parts[parts.length - 1].toUpperCase();
      if (region.length === 2) {
        if (lang === 'en' && EN_AMBIGUOUS_REGIONS[region]) {
          return null;
        }
        if (available[region]) {
          return region;
        }
      }
    }

    var mapped = LANG_TO_COUNTRY[lang];
    if (mapped && available[mapped]) {
      return mapped;
    }

    return null;
  }

  function inferCountryFromLanguages() {
    var tags = []
      .concat(navigator.languages || [])
      .concat(navigator.language ? [navigator.language] : [])
      .concat(navigator.userLanguage ? [navigator.userLanguage] : []);

    var seen = Object.create(null);
    var i;
    var tag;
    var country;

    for (i = 0; i < tags.length; i++) {
      tag = normalizeTag(tags[i]);
      if (!tag || seen[tag]) {
        continue;
      }
      seen[tag] = true;
      country = countryFromTag(tag);
      if (country) {
        return country;
      }
    }

    return null;
  }

  function inferCountryFromTimezone() {
    var tz;
    var i;
    var entry;

    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      return null;
    }

    if (!tz) {
      return null;
    }

    for (i = 0; i < TZ_PREFIX_TO_COUNTRY.length; i++) {
      entry = TZ_PREFIX_TO_COUNTRY[i];
      if (tz.indexOf(entry.prefix) === 0 && available[entry.country]) {
        return entry.country;
      }
    }

    return null;
  }

  function inferCountry() {
    return inferCountryFromLanguages() || inferCountryFromTimezone();
  }

  var inferred = inferCountry();

  if (!inferred || inferred === config.currentCountry) {
    return;
  }

  sessionStorage.setItem(SESSION_KEY, inferred);

  function submitLocalization(countryCode) {
    var form = document.createElement('form');
    form.method = 'POST';
    form.action = '/localization';
    form.acceptCharset = 'UTF-8';
    form.style.display = 'none';

    var fields = {
      form_type: 'localization',
      utf8: '✓',
      country_code: countryCode,
      return_to: window.location.pathname + window.location.search + window.location.hash,
    };

    Object.keys(fields).forEach(function (name) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = fields[name];
      form.appendChild(input);
    });

    document.documentElement.appendChild(form);
    form.submit();
  }

  submitLocalization(inferred);
})();
